import { Link as RouterLink, Link } from "react-router-dom";
import PropTypes from "prop-types";
import {
  AppBar,
  Toolbar,
  useTheme,
  useMediaQuery,
  Box,
  MenuItem,
  Button,
  Menu,
  ListItemIcon,
  ListItemText,
  IconButton,
  Badge,
  Tooltip,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import loadingImage from "../../media/ch-white-icon.png";
import CustomLogout from "./CustomLogout";
import { useState } from "react";
import {
  ExpandLess,
  ExpandMore,
  Search as SearchIcon,
  Close as CloseIcon,
  Menu as MenuIcon,
  Settings as SettingsIcon,
} from "@mui/icons-material";
import useAuth from "../../hooks/useAuth";
import { useChat } from "../../contexts/ChatContext";
import PurchasesSearchBar from "./PurchasesSearchBar";
import useSettings from "../../hooks/useSettings";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import LightModeIcon from "@mui/icons-material/LightMode";
import { NAV_ACCOUNT, NAV_HOME, buildNavbarMenus } from "../../lib/nav.config";
import { getNavIcon } from "../../lib/navIcons";

const NavLinks = styled("div")(({ theme }) => ({
  marginLeft: "auto",
  display: "flex",
  flexGrow: 0,
  flexShrink: 0,
  gap: 2,
  "& .MuiButton-root": {
    color:
      theme.palette.mode === "light" ? "#ffffff" : theme.palette.text.primary,
    minWidth: 0,
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
  },
  "& .MuiSvgIcon-root": {
    color:
      theme.palette.mode === "light" ? "#ffffff" : theme.palette.text.primary,
  },
}));

const Logo = styled("div")({
  flexGrow: "1",
  cursor: "pointer",
});

const StyledLink = styled("a")(({ theme }) => ({
  textDecoration: "none",
  color:
    theme.palette.mode === "light" ? "#ffffff" : theme.palette.text.primary,
  fontSize: "15px",
  display: "flex",
  alignItems: "center",
  marginLeft: theme.spacing(1),
  "&:hover": {
    borderBottom: `1px solid ${
      theme.palette.mode === "light" ? "#ffffff" : theme.palette.text.primary
    }`,
    opacity: 0.85,
  },
}));

const DashboardNavbarRoot = styled(AppBar)(({ theme }) => ({
  ...(theme.palette.mode === "light" && {
    backgroundColor: theme.palette.primary.main,
    boxShadow: "none",
    color: theme.palette.primary.contrastText,
    "& .MuiButton-root": {
      color: theme.palette.primary.contrastText,
    },
    "& .MuiSvgIcon-root": {
      color: theme.palette.primary.contrastText,
    },
  }),
  ...(theme.palette.mode === "dark" && {
    backgroundColor: theme.palette.background.paper,
    borderBottom: `1px solid ${theme.palette.divider}`,
    boxShadow: "none",
    "& .MuiButton-root": {
      color: theme.palette.text.primary,
    },
    "& .MuiSvgIcon-root": {
      color: theme.palette.text.primary,
    },
  }),
  zIndex: theme.zIndex.drawer + 1,
  height: 64,
  display: "flex",
  justifyContent: "center",
}));

// Between lg (1200) and xl (1536). Search collapses at xl first so the
// dropdowns can stay visible on laptop widths.
export const DASHBOARD_NAV_HAMBURGER_PX = 1280;

const DashboardNavbar = (props) => {
  const { onSidebarMobileOpen, ...other } = props;
  const { user } = useAuth();
  const { totalWaiting, waitingResponseCounts } = useChat();
  const isAsync = user?.is_async;
  const roles = user?.roles ?? [];
  const theme = useTheme();
  const isMobile = useMediaQuery(
    theme.breakpoints.down(DASHBOARD_NAV_HAMBURGER_PX)
  );
  const isCompact = useMediaQuery(theme.breakpoints.down("xl"));
  const showInlineSearch = !isMobile && !isCompact;
  const searchIconMode = isMobile || isCompact;
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  const navIconButtonSx = {
    color: (navTheme) =>
      navTheme.palette.mode === "light"
        ? navTheme.palette.primary.contrastText
        : navTheme.palette.text.primary,
  };

  const handleSearchToggle = () => {
    setIsSearchOpen((prev) => !prev);
  };

  const { settings, saveSettings } = useSettings();
  const [settingsMenu, setSettingsMenu] = useState(null);
  const openSettings = Boolean(settingsMenu);

  const handleSettingsClick = (event) => {
    setSettingsMenu(event.currentTarget);
  };

  const handleSettingsClose = () => {
    setSettingsMenu(null);
  };

  const handleThemeChange = () => {
    saveSettings({
      ...settings,
      theme: settings.theme === "LIGHT" ? "DARK" : "LIGHT",
    });
    handleSettingsClose();
  };

  const menus = buildNavbarMenus(roles, { isAsync });
  const [openMenu, setOpenMenu] = useState({ id: null, anchor: null });

  const handleOpenMenu = (id) => (event) => {
    setOpenMenu({ id, anchor: event.currentTarget });
  };

  const handleCloseMenu = () => {
    setOpenMenu({ id: null, anchor: null });
  };

  return (
    <DashboardNavbarRoot {...other}>
      <Toolbar
        sx={{
          height: 64,
          left: 0,
          px: 2,
          width: "100%",
        }}
      >
        <Box sx={{ flexGrow: 1, display: "flex", alignItems: "center" }}>
          {searchIconMode && isSearchOpen ? (
            <Box sx={{ flexGrow: 1 }}>
              <PurchasesSearchBar />
            </Box>
          ) : (
            <>
              <RouterLink to="/" className={Logo}>
                <img
                  src={loadingImage}
                  alt="chooselife"
                  height="40"
                  width="40"
                  display="inline"
                />
              </RouterLink>
              <Box
                sx={{
                  flexGrow: 1,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                {showInlineSearch && <PurchasesSearchBar />}
              </Box>
            </>
          )}
        </Box>

        {searchIconMode && isSearchOpen ? (
          <IconButton
            onClick={handleSearchToggle}
            sx={navIconButtonSx}
          >
            <CloseIcon />
          </IconButton>
        ) : isMobile ? (
          <>
            <IconButton
              onClick={handleSearchToggle}
              sx={navIconButtonSx}
            >
              <SearchIcon />
            </IconButton>
            <IconButton
              onClick={onSidebarMobileOpen}
              sx={navIconButtonSx}
            >
              <MenuIcon />
            </IconButton>
          </>
        ) : (
          <>
            {isCompact && (
              <IconButton
                onClick={handleSearchToggle}
                sx={navIconButtonSx}
              >
                <SearchIcon />
              </IconButton>
            )}
            <NavLinks>
              <Button
                component={Link}
                to={NAV_HOME.path}
              >
                HOME
              </Button>

              {menus.map((group) => {
                const isOpen = openMenu.id === group.id;
                return (
                  <Box key={group.id} component="span">
                    <Button
                      id={`${group.id}-button`}
                      aria-controls={isOpen ? `${group.id}-menu` : undefined}
                      aria-haspopup="true"
                      aria-expanded={isOpen ? "true" : undefined}
                      component={StyledLink}
                      onClick={handleOpenMenu(group.id)}
                      sx={group.showWaitingTotal ? { display: "flex", alignItems: "center", gap: 1.5 } : undefined}
                    >
                      {group.title.toUpperCase()}
                      {group.showWaitingTotal && totalWaiting > 0 && (
                        <Box
                          component="span"
                          sx={{
                            bgcolor: "error.main",
                            color: "error.contrastText",
                            px: 1,
                            py: 0.25,
                            borderRadius: 1,
                            fontSize: "0.75rem",
                            fontWeight: 600,
                            lineHeight: 1.2,
                          }}
                        >
                          {totalWaiting}
                        </Box>
                      )}
                      {isOpen ? <ExpandLess /> : <ExpandMore />}
                    </Button>
                    <Menu
                      id={`${group.id}-menu`}
                      anchorEl={isOpen ? openMenu.anchor : null}
                      open={isOpen}
                      onClose={handleCloseMenu}
                      anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "left",
                      }}
                      transformOrigin={{
                        vertical: "top",
                        horizontal: "left",
                      }}
                    >
                      {group.items.map((item) => {
                        const badgeCount = item.badge
                          ? waitingResponseCounts?.[item.badge]
                          : 0;
                        return (
                          <MenuItem
                            key={item.path}
                            component={Link}
                            to={item.path}
                            onClick={handleCloseMenu}
                          >
                            <ListItemIcon>{getNavIcon(item.icon)}</ListItemIcon>
                            <ListItemText
                              primary={item.title}
                              sx={item.badge ? { "& .MuiListItemText-primary": { mr: 1.5 } } : undefined}
                            />
                            {badgeCount > 0 && (
                              <Badge badgeContent={badgeCount} color="primary" sx={{ ml: 0.5 }} />
                            )}
                          </MenuItem>
                        );
                      })}
                    </Menu>
                  </Box>
                );
              })}
              <Tooltip title="Settings">
                <IconButton
                  id="settings-button"
                  aria-controls={openSettings ? "settings-menu" : undefined}
                  aria-haspopup="true"
                  aria-expanded={openSettings ? "true" : undefined}
                  onClick={handleSettingsClick}
                  sx={navIconButtonSx}
                >
                  <SettingsIcon />
                </IconButton>
              </Tooltip>
              <Menu
                id="settings-menu"
                anchorEl={settingsMenu}
                open={openSettings}
                onClose={handleSettingsClose}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "right",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
              >
                <MenuItem onClick={handleThemeChange}>
                  <ListItemIcon>
                    {settings.theme === "LIGHT" ? (
                      <DarkModeIcon />
                    ) : (
                      <LightModeIcon />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={`Switch to ${
                      settings.theme === "LIGHT" ? "Dark" : "Light"
                    } Mode`}
                  />
                </MenuItem>
                <MenuItem
                  component={RouterLink}
                  to={NAV_ACCOUNT.path}
                  onClick={handleSettingsClose}
                >
                  {NAV_ACCOUNT.title}
                </MenuItem>
              </Menu>
              <Box sx={{ ml: 2 }}>
                <CustomLogout />
              </Box>
            </NavLinks>
          </>
        )}
      </Toolbar>
    </DashboardNavbarRoot>
  );
};

DashboardNavbar.propTypes = {
  onSidebarMobileOpen: PropTypes.func,
};

export default DashboardNavbar;
